import React, { useState, useEffect } from 'react';
import { FiX, FiMail, FiCheckCircle } from 'react-icons/fi';

import { useApplyToGig } from '../../api/mutations';
import { Button } from '../ui/Button';
import { RichTextEditor } from '../ui/RichTextEditor';

// Minimal gig shape needed by this modal — compatible with both GigItem and the full API gig object
export interface ApplyGigData {
  id?: string;
  gig_uuid?: string;
  title?: string;
  location_name?: string;
  location?: { city?: string; state?: string; country?: string; street?: string } | null;
  budget_type?: string;
  budget_amount?: string | null;
  budget_min?: string | null;
  budget_max?: string | null;
  start_datetime?: string;
  end_datetime?: string | null;
  timezone?: string | null;
  status?: string | null;
  application_start_datetime?: string | null;
  application_end_datetime?: string | null;
  can_apply?: boolean | string | number | null;
  canApply?: boolean | string | number | null;
  owner?: {
    full_name?: string;
    username?: string;
  };
}

function formatDate(isoString?: string): string {
  if (!isoString) return '[Date]';
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(isoString));
}

interface ApplyGigModalProps {
  isOpen: boolean;
  onClose: () => void;
  gig: ApplyGigData | null;
  /** The route param ID (gig_uuid or id) used to navigate to /gigs/$gigId */
  gigId: string;
  user?: { email?: string; fullName?: string; username?: string } | null;
}

export const ApplyGigModal: React.FC<ApplyGigModalProps> = ({
  isOpen,
  onClose,
  gig,
  gigId,
  user,
}) => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const { mutate: applyToGig, isPending } = useApplyToGig();

  // Sync email when user changes or modal opens
  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
    }
  }, [user, isOpen]);

  // Re-build the pre-filled message whenever the gig changes
  useEffect(() => {
    if (!gig || !isOpen) return;
    const organizerName = gig.owner?.full_name || gig.owner?.username || '[Organizer Name]';
    const title = gig.title || '[Gig Title]';
    const location =
      gig.location_name ||
      [gig.location?.city, gig.location?.state].filter(Boolean).join(', ') ||
      '[Location]';
    const date = formatDate(gig.start_datetime);

    const profileRef = user?.username ? `@${user.username}` : '[Your profile username]';
    const userEmail = user?.email || '[Your email]';

    setMessage(
      `Hi ${organizerName},\n\nI'm applying for "${title}" scheduled for ${date} in ${location}.\n\nAbout Me\n[Brief introduction — who you are, what you do, years of experience]\n\nStyle / Genre\n[Your sound or specialty — e.g. Afrobeats, Amapiano, R&B]\n\nPortfolio\nMusicade Profile: ${profileRef}\n\nAvailability\n[Confirm your availability for the specified date and time]\n\nContact Details\nEmail: ${userEmail}`,
    );
    setIsSuccess(false);
  }, [gig, isOpen]);

  if (!isOpen || !gig) return null;

  const handleSubmit = () => {
    if (!email) return;

    applyToGig(
      {
        gigUuid: gig.gig_uuid || gigId || '',
        data: {
          email,
          message_html: /<[a-z][\s\S]*>/i.test(message) ? message : message.replace(/\n/g, '<br>'),
          screen_width: window.screen.width,
          screen_height: window.screen.height,
        },
      },
      {
        onSuccess: () => {
          setIsSuccess(true);
        },
      },
    );
  };

  const handleModalClose = () => {
    setIsSuccess(false);
    onClose();
  };

  if (isSuccess) {
    return (
      <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-0 sm:p-4">
        <div className="w-full sm:max-w-lg bg-[#0c0c0c] border border-white/10 rounded-t-3xl sm:rounded-[32px] overflow-hidden shadow-2xl">
          <div className="flex justify-end px-5 pt-5 sm:p-6 sm:pb-0">
            <button
              onClick={handleModalClose}
              className="p-2 text-zinc-500 hover:text-white transition-colors rounded-full hover:bg-white/5"
            >
              <FiX size={22} />
            </button>
          </div>
          <div className="flex flex-col items-center justify-center px-6 pb-12 sm:px-10 sm:pb-16 text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-500/10 rounded-full flex items-center justify-center mb-5 sm:mb-6">
              <FiCheckCircle className="text-green-500 w-10 h-10 sm:w-12 sm:h-12" />
            </div>
            <h2 className="text-white text-2xl sm:text-3xl font-bold mb-2 sm:mb-3">
              Application Sent
            </h2>
            <p className="text-zinc-400 text-base sm:text-lg mb-6 sm:mb-8 max-w-xs">
              Your application has been submitted to the gig creator
            </p>
            <div className="bg-zinc-900/50 border border-white/5 rounded-2xl px-5 py-3.5 sm:px-6 sm:py-4">
              <p className="text-zinc-500 text-xs sm:text-sm">
                You'll be notified once the creator reviews your application
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-0 sm:p-4">
      <div className="w-full sm:max-w-2xl bg-[#0c0c0c] border border-white/10 rounded-t-3xl sm:rounded-[32px] overflow-hidden shadow-2xl flex flex-col max-h-[90vh] sm:max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 sm:px-8 sm:pt-8 sm:pb-4">
          <h3 className="text-white text-lg sm:text-xl font-bold">Apply for Gig</h3>
          <button
            onClick={onClose}
            className="p-2 text-zinc-500 hover:text-white transition-colors rounded-full hover:bg-white/5"
          >
            <FiX size={22} />
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="px-5 pb-5 sm:px-8 sm:pb-8 flex-1 overflow-y-auto space-y-4 sm:space-y-6">
          {/* Email Input */}
          <div className="space-y-1.5 sm:space-y-2">
            <label className="text-zinc-400 text-[11px] sm:text-xs font-semibold uppercase tracking-wider">
              Your Email Address *
            </label>
            <div className="relative group">
              <div className="absolute left-3.5 sm:left-4 top-1/2 -translate-y-1/2 text-zinc-500 group-focus-within:text-blue-400 transition-colors">
                <FiMail size={16} />
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your Email address"
                className="w-full h-12 sm:h-14 bg-zinc-900/40 border border-white/10 rounded-xl sm:rounded-2xl pl-11 sm:pl-12 pr-4 text-sm sm:text-base text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
              />
            </div>
          </div>

          {/* Rich Text Editor */}
          <RichTextEditor
            value={message}
            onChange={setMessage}
            placeholder="Tell the organizer about yourself..."
            minHeight="220px"
          />
        </div>

        {/* Footer */}
        <div className="px-5 py-5 sm:px-8 sm:py-6 border-t border-white/5 bg-zinc-900/20 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <p
              className="text-xs sm:text-sm font-bold leading-tight text-center sm:text-left"
              style={{ color: '#F7963B' }}
            >
              Once submitted, you will not be able to edit this application.
            </p>
          </div>
          <Button
            onClick={handleSubmit}
            disabled={isPending || !email.trim() || !message.trim()}
            className="h-11 px-8 sm:h-12 sm:px-10 rounded-full bg-white text-black hover:bg-zinc-200 font-bold text-xs sm:text-sm tracking-tight shadow-lg shadow-white/5 transition-all active:scale-95 disabled:opacity-50 disabled:active:scale-100 w-full sm:w-auto"
          >
            {isPending ? 'Submitting...' : 'Submit application'}
          </Button>
        </div>
      </div>
    </div>
  );
};
